import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../services/api';

const TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf']);
export function identityDocumentError(data) {
  const kind = data.get('document_type');
  if (!['driver_license', 'passport', 'id_card'].includes(kind)) return 'Choose a valid ID type.';
  for (const [key, label] of [['identity_photo', 'Selfie'], ['document_front', 'Document front'], ['document_back', 'Document back']]) {
    const file = data.get(key);
    if (!file?.size) {
      if (key !== 'document_back' || kind !== 'passport') return `${label} is required.`;
      continue;
    }
    if (file.size > 8 * 1024 * 1024) return `${label} must be 8 MB or smaller.`;
    if (!TYPES.has(file.type)) return `${label}: use JPEG, PNG, WebP, HEIC/HEIF or PDF.`;
  }
  return '';
}

export default function useIdentityVerification(scopeKey = '') {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const generation = useRef(0), scope = useRef(0), request = useRef(null), submitLock = useRef(false);
  useEffect(() => {
    scope.current++; generation.current++; request.current?.abort();
    setStatus(null); setLoading(false); setError(''); setSubmitting(false); setUncertain(false); submitLock.current = false;
    return () => { scope.current++; generation.current++; request.current?.abort(); };
  }, [scopeKey]);

  const refresh = useCallback(async () => {
    const version = ++generation.current;
    request.current?.abort(); const controller = new AbortController(); request.current = controller;
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/api/wallet-transfer/status', { timeout: 15000, signal: controller.signal });
      if (version !== generation.current) return null;
      if (!['not_submitted', 'pending', 'approved', 'declined'].includes(data?.kyc?.status)) throw new Error('Invalid status');
      setStatus(data);
      // A declined record may predate an uncertain resubmission. It is not
      // evidence that the latest upload was rejected or safe to repeat.
      if (['pending', 'approved'].includes(data.kyc.status)) setUncertain(false);
      return data;
    } catch {
      if (version === generation.current) setError('Could not check verification status. Please retry.');
      return null;
    } finally { if (version === generation.current) setLoading(false); }
  }, []);

  const submit = async data => {
    if (submitLock.current || uncertain || loading || !status || !['not_submitted', 'declined'].includes(status.kyc.status)) return false;
    const validation = identityDocumentError(data);
    if (validation) { setError(validation); return false; }
    submitLock.current = true; setSubmitting(true); setError('');
    const ownScope = scope.current;
    try {
      const response = await api.post('/api/wallet-transfer/kyc', data, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      if (scope.current !== ownScope) return false;
      if (response.data?.success !== true || response.data?.kyc?.status !== 'pending') throw new Error('Unconfirmed submission');
      generation.current++; request.current?.abort(); setLoading(false);
      setStatus(previous => ({ ...previous, kyc: response.data.kyc })); setUncertain(false);
      return true;
    } catch (err) {
      if (scope.current !== ownScope) return false;
      const code = err.response?.status;
      if ([400, 401, 403, 413, 415, 422].includes(code)) {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Check your files and account access, then try again.');
      } else {
        setUncertain(true);
        const latest = await refresh();
        if (scope.current !== ownScope) return false;
        if (['pending', 'approved'].includes(latest?.kyc?.status)) return false;
        setError('Submission outcome is unconfirmed. Check status or contact support; do not submit again yet.');
      }
      return false;
    } finally {
      if (scope.current === ownScope) { submitLock.current = false; setSubmitting(false); }
    }
  };
  return { status, loading, error, submitting, uncertain, refresh, submit };
}
